/**
 * @Author: AI Assistant
 * @Date: 2025-07-17
 * @Description: 数字人(Agent)相关接口
 */
import axios from 'axios'
import Cookies from 'js-cookie'
import JSEncrypt from 'jsencrypt'
import { getToken } from '@/utils/auth'
import store from '@/store'

// 缓存工作区 ID
let _cachedWorkspaceId = null

/**
 * 创建独立的 axios 实例，不携带门户的 Authorization 头
 * 避免与 @/utils/request 的门户 token 拦截器冲突
 */
const agentRequest = axios.create({
  timeout: 50000,
  withCredentials: false
})

// 401 重试状态
let _isRefreshingToken = false
let _pendingRequests = []

// 共享刷新 Promise：并发触发 401 时只发一次注册请求
let _refreshTokenPromise = null

/**
 * 刷新 tianzhiToken（与 agentAuth/fetchAgentToken 逻辑一致），仅在本模块内使用
 */
async function refreshTianzhiToken() {
  if (_refreshTokenPromise) return _refreshTokenPromise
  _refreshTokenPromise = (async () => {
    const token = getToken()
    if (!token) throw new Error('未获取到门户 token')

    // 1. 获取 RSA 公钥
    const pubRes = await agentRequest({
      url: '/agent-api/api/v1/user/public_key',
      method: 'GET',
      headers: {
        ApiKey: 'Z0MsqugCI78t4voc9Q4ftdqtD4UdOuOY'
      }
    })
    const publicKey = pubRes.data?.public_key || pubRes.public_key
    if (!publicKey) throw new Error('获取公钥失败')

    // 2. 加密密码
    const encrypt = new JSEncrypt()
    encrypt.setPublicKey(publicKey)
    const password = encrypt.encrypt('Cosmoplat@2024')
    if (!password) throw new Error('密码加密失败')

    // 3. 调用注册接口获取新 token
    const regRes = await agentRequest({
      url: '/agent-api/api/v1/user/new_c_regist',
      method: 'POST',
      data: {
        user_name: '任务',
        password,
        captcha_key: '',
        third_token: token
      }
    })
    const regData = regRes.data || regRes
    if (!regData || !regData.access_token) {
      throw new Error('注册接口未返回 access_token')
    }

    // 4. 存储新 token（仅存 cookie，key 从环境变量读取）
    const agentTokenKey =
      process.env.VUE_APP_WS_TOKEN_KEY || 'tianzhi_test_agent_token'
    const accessToken = regData.access_token
    Cookies.set(agentTokenKey, accessToken, {
      path: '/',
      SameSite: 'None',
      Secure: true
    })

    if (regData.third_user_name) {
      localStorage.setItem('cosmo_claw_user_name', regData.third_user_name)
    }
    if (regData.platform_role) {
      localStorage.setItem('platform_role', regData.platform_role)
    }

    return accessToken
  })()
  try {
    return await _refreshTokenPromise
  } finally {
    _refreshTokenPromise = null
  }
}

/**
 * 清除 config 上的 Authorization 头，让请求拦截器用当前 cookie 中的 token 重新设置
 */
function clearAuthHeader(config) {
  const headers = config?.headers
  if (!headers) return
  if (typeof headers.delete === 'function') {
    headers.delete('Authorization')
  } else {
    delete headers.Authorization
  }
}

/**
 * 请求拦截器：token 为空时先现场获取（fetchAgentToken），保证首次请求就带上有效 token，
 * 不会发出 "Bearer "（空 token）头导致 401。
 * /intelligent-agent/ 路径用裸 token，其余路径用 Bearer 前缀（与 agentAuth 一致）。
 */
agentRequest.interceptors.request.use(async (config) => {
  let tianzhiToken = getCurrentTianzhiToken()
  // 首次打开数字人页 / 会话 cookie 被清空时，token 可能为空，先换取再发请求
  if (!tianzhiToken) {
    try {
      await store.dispatch('user/fetchAgentToken')
      tianzhiToken = getCurrentTianzhiToken()
    } catch (e) {
      console.warn('[digitalHuman] 初始化 agent token 失败:', e)
    }
  }
  if (tianzhiToken) {
    if (config.url && config.url.includes('/intelligent-agent/')) {
      config.headers['Authorization'] = tianzhiToken
    } else {
      config.headers['Authorization'] = 'Bearer ' + tianzhiToken
    }
  } else {
    clearAuthHeader(config)
  }
  return config
})

/**
 * 响应拦截器：提取 response.data，处理 401 自动刷新 token 并重试，统一业务错误
 */
agentRequest.interceptors.response.use(
  (response) => {
    const res = response.data
    if (
      res.code === 200 ||
      res.code === '200' ||
      res.code === 0 ||
      res.status_code === 200
    ) {
      return res
    }
    // 业务错误，抛出异常
    const msg = res.msg || res.message || '请求失败'
    return Promise.reject(new Error(msg))
  },
  (error) => {
    const originalRequest = error.config

    // 401 且未重试过：刷新 tianzhiToken 后重试
    if (error.response?.status === 401 && !originalRequest?._tianzhiRetry) {
      originalRequest._tianzhiRetry = true

      // 正在刷新则排队等待
      if (_isRefreshingToken) {
        return new Promise((resolve) => {
          _pendingRequests.push(() => {
            clearAuthHeader(originalRequest)
            resolve(agentRequest(originalRequest))
          })
        })
      }

      _isRefreshingToken = true
      return refreshTianzhiToken()
        .then((newToken) => {
          // 重试排队的请求
          _pendingRequests.forEach((cb) => cb(newToken))
          _pendingRequests = []
          // 重试当前请求
          clearAuthHeader(originalRequest)
          return agentRequest(originalRequest)
        })
        .catch((refreshErr) => {
          console.warn('[digitalHuman] 刷新 tianzhiToken 失败:', refreshErr)
          _pendingRequests = []
          const msg =
            error?.response?.data?.msg ||
            error?.response?.data?.message ||
            error.message ||
            '网络请求失败'
          return Promise.reject(new Error(msg))
        })
        .finally(() => {
          _isRefreshingToken = false
        })
    }

    const msg =
      error?.response?.data?.msg ||
      error?.response?.data?.message ||
      error.message ||
      '网络请求失败'
    return Promise.reject(new Error(msg))
  }
)

/**
 * 获取当前存储的 tianzhiToken
 */
function getCurrentTianzhiToken() {
  const agentTokenKey = process.env.VUE_APP_WS_TOKEN_KEY || 'tianzhi_test_agent_token'
  return Cookies.get(agentTokenKey) || ''
}

/**
 * 获取当前工作区 ID
 * 从环境变量 VUE_APP_DIGITAL_HUMAN_WS_ID 获取运营端数字人管理工作区ID
 * 各环境固定值：
 *   dev/test -> 2078050434079305730
 *   pre     -> 2079843084755173377
 *   prod    -> 2079847029239447553
 */
function getWorkspaceId() {
  if (!_cachedWorkspaceId) {
    _cachedWorkspaceId = process.env.VUE_APP_DIGITAL_HUMAN_WS_ID || ''
  }
  return _cachedWorkspaceId
}

/**
 * 获取Agent列表
 * @param {Object} params 查询参数
 * @param {Boolean} params.enabled 按启用状态过滤（可选）
 * @param {Number} params.workspaceId 工作区ID（可选，不传默认1）
 * @returns {Promise} Promise对象
 */
export function getAgentList(params = {}) {
  const { ...rest } = params
  const tianzhiToken = getCurrentTianzhiToken()

  return agentRequest({
    url: '/agent-api/claw/api/v1/agents',
    method: 'get',
    params: rest,
    headers: {
      'Authorization': `Bearer ${tianzhiToken}`,
      'X-Workspace-Id': getWorkspaceId()
    }
  })
}

/**
 * 获取Agent详情
 * @param {Number|String} id Agent ID
 * @param {Number|String} [workspaceId] 工作区ID（可选，不传则使用环境变量配置的工作区ID）
 * @returns {Promise} Promise对象
 */
export function getAgentDetail(id, workspaceId) {
  const tianzhiToken = getCurrentTianzhiToken()

  return agentRequest({
    url: `/agent-api/claw/api/v1/agents/${id}`,
    method: 'get',
    headers: {
      'Authorization': `Bearer ${tianzhiToken}`,
      'X-Workspace-Id': workspaceId || getWorkspaceId()
    }
  })
}

/**
 * 获取已启用的模型 Provider 列表
 * @returns {Promise} Promise对象，data 为 Provider 列表
 */
export function getModelProviders() {
  const tianzhiToken = getCurrentTianzhiToken()

  return agentRequest({
    url: '/agent-api/claw/api/v1/models',
    method: 'get',
    headers: {
      'Authorization': `Bearer ${tianzhiToken}`,
      'X-Workspace-Id': getWorkspaceId()
    }
  })
}

/**
 * 获取Agent会话统计（今日会话 / 累计会话次数）
 * @param {Number|String} id Agent ID
 * @returns {Promise} Promise对象，data 包含 { today, total }
 */
export function getAgentConversationStats(id, workspaceId) {
  const tianzhiToken = getCurrentTianzhiToken()

  return agentRequest({
    url: `/agent-api/claw/api/v1/agents/${id}/conversation-stats`,
    method: 'get',
    headers: {
      'Authorization': `Bearer ${tianzhiToken}`,
      'X-Workspace-Id': workspaceId || getWorkspaceId()
    }
  })
}

/**
 * 更新Agent
 * @param {Number|String} id Agent ID
 * @param {Object} data 更新的字段
 * @param {Number|String} [workspaceId] 工作区ID（可选，不传则自动获取）
 * @returns {Promise} Promise对象
 */

export function updateAgent(id, data, workspaceId) {
  const tianzhiToken = getCurrentTianzhiToken()

  return agentRequest({
    url: `/agent-api/claw/api/v1/agents/${id}`,
    method: 'put',
    data,
    headers: {
      'Authorization': `Bearer ${tianzhiToken}`,
      'X-Workspace-Id': workspaceId || getWorkspaceId(),
      'Content-Type': 'application/json'
    }
  })
}

/**
 * 创建Agent
 * @param {Object} data 创建参数
 * @param {String} data.name Agent名称（必须）
 * @param {String} data.agentType Agent类型（必须）枚举: react, plan_execute
 * @param {String} [data.icon] 图标
 * @param {String} [data.description] 描述
 * @param {String} [data.tags] 标签
 * @param {Boolean} [data.enabled] 是否启用，默认true
 * @param {String} [data.modelName] 模型名称
 * @param {Number} [data.maxIterations] 最大迭代次数
 * @param {String} [data.defaultThinkingLevel] 默认思考深度 枚举: off,low,medium,high,max
 * @param {String} [data.systemPrompt] 系统提示词
 * @param {Number} [data.sortOrder] 排序值
 * @param {Boolean} [data.skillsDisabled] 禁用所有skill
 * @param {Boolean} [data.toolsDisabled] 禁用所有非系统工具
 * @param {Number|String} [workspaceId] 工作区ID（可选，不传则自动获取）
 * @returns {Promise} Promise对象
 */
export function createAgent(data, workspaceId) {
  const tianzhiToken = getCurrentTianzhiToken()

  return agentRequest({
    url: '/agent-api/claw/api/v1/agents',
    method: 'post',
    data,
    headers: {
      'Authorization': `Bearer ${tianzhiToken}`,
      'X-Workspace-Id': workspaceId || getWorkspaceId(),
      'Content-Type': 'application/json'
    }
  })
}

/**
 * 删除Agent
 * @param {Number|String} id Agent ID
 * @returns {Promise} Promise对象
 */
export function deleteAgent(id) {
  const tianzhiToken = getCurrentTianzhiToken()

  return agentRequest({
    url: `/agent-api/claw/api/v1/agents/${id}`,
    method: 'delete',
    headers: {
      'Authorization': `Bearer ${tianzhiToken}`,
      'X-Workspace-Id': getWorkspaceId()
    }
  })
}

/**
 * 数字人上架/下架
 * @param {Number|String} id Agent ID
 * @param {Object} data 参数
 * @param {Number} data.status 状态：0-下架 1-上架
 * @param {Number} [data.price] 价格（上架时必填）
 * @param {Number|String} [workspaceId] 工作区ID（可选，不传则自动获取）
 * @returns {Promise} Promise对象
 */
export function updateAgentShelfStatus(id, data, workspaceId) {
  const tianzhiToken = getCurrentTianzhiToken()

  return agentRequest({
    url: `/agent-api/claw/api/v1/agents/${id}/shelf-status`,
    method: 'put',
    data,
    headers: {
      'Authorization': `Bearer ${tianzhiToken}`,
      'X-Workspace-Id': workspaceId || getWorkspaceId(),
      'Content-Type': 'application/json'
    }
  })
}

/**
 * 技能广场-查询skill分页
 * @param {Object} params 查询参数
 * @param {String} [params.name] 名称
 * @param {Number} [params.industryId] 行业id
 * @param {Number} [params.sceneId] 类型id
 * @param {Number} [params.status] 发布状态，0-未发布 1-上架中 2-待审核 3-审核驳回
 * @param {Number} [params.digitalHuman] 是否显示在爆款数字人技能列表，0-否，1-是
 * @param {Number} [params.pageNum] 分页页码
 * @param {Number} [params.pageSize] 分页大小
 * @returns {Promise} Promise对象
 */
export function getSkillSquarePage(params = {}) {
  const tianzhiToken = getCurrentTianzhiToken()

  return agentRequest({
    url: '/agent-api/intelligent-agent/api/v1/assistant/skill/square/page',
    method: 'get',
    params,
    headers: {
      'Authorization': `${tianzhiToken}`,
      'X-Workspace-Id': getWorkspaceId()
    }
  })
}

/**
 * 查询Agent已绑定的技能列表
 * @param {Number|String} agentId Agent ID
 * @returns {Promise} Promise对象
 */
export function getAgentSkills(agentId, workspaceId) {
  const tianzhiToken = getCurrentTianzhiToken()

  return agentRequest({
    url: `/agent-api/claw/api/v1/agents/${agentId}/skills`,
    method: 'get',
    headers: {
      'Authorization': `Bearer ${tianzhiToken}`,
      'X-Workspace-Id': workspaceId || getWorkspaceId()
    }
  })
}

/**
 * 批量保存Agent技能绑定（覆盖式）
 * @param {Number|String} agentId Agent ID
 * @param {Array<String>} skillIds 技能ID数组
 * @returns {Promise} Promise对象
 */
/**
 * 获取Agent工具列表
 * @param {Number|String} agentId Agent ID
 * @returns {Promise} Promise对象，data 为工具列表
 */
export function getAgentTools(agentId, workspaceId) {
  const tianzhiToken = getCurrentTianzhiToken()

  return agentRequest({
    url: `/agent-api/claw/api/v1/agents/${agentId}/tools`,
    method: 'get',
    headers: {
      'Authorization': `Bearer ${tianzhiToken}`,
      'X-Workspace-Id': workspaceId || getWorkspaceId()
    }
  })
}

export function saveAgentSkills(agentId, skillIds, workspaceId) {
  const tianzhiToken = getCurrentTianzhiToken()

  return agentRequest({
    url: `/agent-api/claw/api/v1/agents/${agentId}/skills`,
    method: 'put',
    data: skillIds,
    headers: {
      'Authorization': `Bearer ${tianzhiToken}`,
      'X-Workspace-Id': workspaceId || getWorkspaceId(),
      'Content-Type': 'application/json'
    }
  })
}

/**
 * 查询IAM用户分页列表
 * @param {Object} [params] 查询参数
 * @param {Number} [params.page] 分页页码，默认1
 * @param {Number} [params.size] 分页大小，默认20
 * @param {String} [params.keyword] 关键词，用户名或姓名模糊匹配
 * @returns {Promise} Promise对象
 */
export function getIamUserPage(params = {}) {
  const tianzhiToken = getCurrentTianzhiToken()

  return agentRequest({
    url: '/agent-api/claw/api/v1/users/iam/page',
    method: 'get',
    params,
    headers: {
      'Authorization': `Bearer ${tianzhiToken}`,
      'X-Workspace-Id': getWorkspaceId()
    }
  })
}

/**
 * 根据用户ID查询其所属工作区
 * @param {String} username 企工用户ID
 * @returns {Promise} Promise对象，返回工作区列表
 */
export function getWorkspaceByUser(username) {
  const tianzhiToken = getCurrentTianzhiToken()

  return agentRequest({
    url: '/agent-api/claw/api/v1/workspaces/by-user',
    method: 'get',
    params: { username },
    headers: {
      'Authorization': `Bearer ${tianzhiToken}`,
      'X-Workspace-Id': getWorkspaceId()
    }
  })
}

/**
 * 查询某个Agent作为模板被哪些用户安装
 * @param {Number|String} agentId Agent ID
 * @returns {Promise} Promise对象
 */
export function getTemplateUsers(agentId) {
  const tianzhiToken = getCurrentTianzhiToken()

  return agentRequest({
    url: `/agent-api/claw/api/v1/agents/${agentId}/template-users`,
    method: 'get',
    headers: {
      'Authorization': `Bearer ${tianzhiToken}`,
      'X-Workspace-Id': getWorkspaceId()
    }
  })
}

/**
 * 将模板数字人的全量配置同步到所有衍生数字人
 * @param {Number|String} agentId 模板Agent ID
 * @returns {Promise} Promise对象，返回更新失败的衍生Agent ID列表
 */
export function templateSync(agentId) {
  const tianzhiToken = getCurrentTianzhiToken()

  return agentRequest({
    url: `/agent-api/claw/api/v1/agents/${agentId}/template-sync`,
    method: 'put',
    headers: {
      'Authorization': `Bearer ${tianzhiToken}`,
      'X-Workspace-Id': getWorkspaceId()
    }
  })
}

/**
 * 保存模板数字人与用户组的关联（全量替换，不创建衍生Agent）
 * @param {Number|String} agentId 模板Agent ID
 * @param {Array<String|Number>} groupIds 用户组ID列表
 * @returns {Promise} Promise对象
 */
export function updateTemplateGroups(agentId, groupIds) {
  const tianzhiToken = getCurrentTianzhiToken()

  return agentRequest({
    url: `/agent-api/claw/api/v1/agents/${agentId}/template-groups`,
    method: 'put',
    data: groupIds,
    headers: {
      'Authorization': `Bearer ${tianzhiToken}`,
      'X-Workspace-Id': getWorkspaceId(),
      'Content-Type': 'application/json'
    }
  })
}

/**
 * 查询模板数字人绑定的用户组列表
 * @param {Number|String} agentId 模板Agent ID
 * @returns {Promise} Promise对象，data 为 [{ groupId, groupName }]
 */
export function getTemplateGroups(agentId) {
  const tianzhiToken = getCurrentTianzhiToken()

  return agentRequest({
    url: `/agent-api/claw/api/v1/agents/${agentId}/template-groups`,
    method: 'get',
    headers: {
      'Authorization': `Bearer ${tianzhiToken}`,
      'X-Workspace-Id': getWorkspaceId()
    }
  })
}

/**
 * 删除模板数字人的某个衍生Agent
 * @param {Number|String} agentId 模板Agent ID
 * @param {Number|String} derivedAgentId 衍生Agent ID
 * @returns {Promise} Promise对象
 */
export function deleteTemplateUserAgent(agentId, derivedAgentId) {
  const tianzhiToken = getCurrentTianzhiToken()

  return agentRequest({
    url: `/agent-api/claw/api/v1/agents/${agentId}/template-users/${derivedAgentId}`,
    method: 'delete',
    headers: {
      'Authorization': `Bearer ${tianzhiToken}`,
      'X-Workspace-Id': getWorkspaceId()
    }
  })
}

/**
 * 将所选用户组关联的模板数字人同步（衍生）给指定用户（只增不删）
 * @param {String|Number} username 用户 IAM id（= mate_user.username）
 * @param {Array<String|Number>} groupIds 所选用户组ID列表
 * @returns {Promise} Promise对象，data 为 { createdAgentIds, skippedTemplateIds, failedTemplates }
 */
export function syncAgentsToUser(username, groupIds) {
  const tianzhiToken = getCurrentTianzhiToken()

  return agentRequest({
    url: '/agent-api/claw/api/v1/agents/sync-to-user',
    method: 'post',
    data: { username, groupIds },
    headers: {
      'Authorization': `Bearer ${tianzhiToken}`,
      'X-Workspace-Id': getWorkspaceId(),
      'Content-Type': 'application/json'
    }
  })
}
